import type { CSSProperties } from "react";
import { TOLERANCE } from "src/business-components/Pit/Pit.constants";
import type {
  PipelineRun,
  RunStatus,
} from "src/queries/pipelines/pipelineRuns.types";
import { areDatesEqualWithinTolerance } from "src/utils/time";
import {
  ITEM_SIZES,
  LEFT_RAIL_GUTTER_PX,
  MAX_ROWS_COUNT,
} from "./PitSelector.constants";
import type {
  EnhancedRun,
  OptionItemData,
  PitSelectorRun,
  RunInfo,
} from "./PitSelector.types";

export const enhancePits = (
  pits: PitSelectorRun[] | undefined,
  withReruns: boolean
): EnhancedRun[] => {
  if (!pits) return [];

  const pitMap: Record<string, number> = {};

  return pits.reduce<EnhancedRun[]>((acc, p) => {
    // Extract "id" based on available properties (MinimalTaskRun has task_id, PipelineRun has last_run)
    const uid =
      "task_id" in p ? p.task_id : ((p as PipelineRun).last_run ?? p.start_time);

    pitMap[p.app_pit] = (pitMap[p.app_pit] || 0) + 1;

    // skip reruns items if "withReruns" is false
    if (!withReruns && pitMap[p.app_pit] > 1) {
      return acc;
    }

    acc.push({ ...p, isRerun: pitMap[p.app_pit] > 1, uid });

    return acc;
  }, []);
};

// CURRENTLY UNUSED
export const getPitsWithReRuns = (runs: PitSelectorRun[] | undefined) => {
  if (!runs) return [];

  // Track each pit and its status, plus collect runs
  const pitMap = new Map<string, { status: RunStatus; runs: RunInfo[] }>();

  runs?.forEach((run) => {
    if (!pitMap.has(run.app_pit)) {
      pitMap.set(run.app_pit, {
        status: run.status as RunStatus,
        runs: [],
      });
    }

    // Extract "id" based on available properties (MinimalTaskRun has task_id, PipelineRun has last_run)
    const id =
      "task_id" in run
        ? run.task_id
        : ((run as PipelineRun).last_run ?? run.start_time);

    // different task runs can have the same "app_pit".
    // Each run is pushed into the "runs" array within the pitMap.
    pitMap.get(run.app_pit)?.runs.push({
      // map "start_time" to "app_pit" within "runs"
      app_pit: run.start_time || (run as PipelineRun).last_run,
      status: run.status,
      id,
    });
  });

  // Sort "runs" array (within each pit item) by "pit" (newest first)
  pitMap.forEach((info) => {
    info.runs.sort((a, b) => b.app_pit.localeCompare(a.app_pit));
  });

  // Convert to array and sort by pit (newest first)
  const result = Array.from(pitMap.entries())
    .map(([app_pit, { status, runs }]) => ({
      app_pit,
      status,
      runs,
    }))
    .sort((a, b) => b.app_pit.localeCompare(a.app_pit));

  return result;
};

/**
 * Calculates the total height of the listbox based on the number of items.
 * It caps the height at MAX_ROWS_COUNT * ITEM_SIZES.sm.
 */
export const getTotalHeight = (itemCount: number) => {
  // using "ITEM_SIZES.md" just to be certain there is enough space for a single item which might be of the larger type
  if (itemCount > MAX_ROWS_COUNT) {
    return MAX_ROWS_COUNT * ITEM_SIZES.md;
  }
  return itemCount * ITEM_SIZES.md;
};

/**
 * Does <Pit/> render its subtitle line for this item? Drives row-height variant
 * selection across PitSelector and downstream consumers (e.g. TaskParamChangesPitSelector).
 *
 * Data-only: render-time flags (`basic`, `onlySubtitle`) are intentionally out of scope —
 * those are presentation concerns local to <Pit>. `isRerun` is included because
 * PitSelectorDefaultRenderOption forwards it as `onlySubtitle` (1-line render).
 *
 * Mirrors <Pit/>'s `shouldRenderSubtitle` logic for the data-driven path.
 */
export const pitHasExtraSubtitleLine = (item: {
  app_pit: string;
  start_time?: string;
  last_run?: string;
  isRerun?: boolean;
}): boolean => {
  if (item.isRerun) return false;
  const ref = item.last_run || item.start_time;
  if (!ref) return false;
  return !areDatesEqualWithinTolerance(item.app_pit, ref, TOLERANCE);
};

/**
 * A row can be either small or large, depending how the underlying <Pit/> component is rendered.
 */
export const getRowHeight = (
  idx: number,
  { itemData }: { itemData: OptionItemData[] }
) => {
  const item = itemData[idx][1];
  const last_run = "last_run" in item ? item.last_run : undefined;
  return pitHasExtraSubtitleLine({ ...item, last_run })
    ? ITEM_SIZES.md
    : ITEM_SIZES.sm;
};

/**
 * Style override applied to the <li> when a left-rail slot is in use.
 * Layered on top of react-window's per-row inline `style` (which sets
 * `top` / `height` / `transform` / `position`), this only narrows the
 * <li>'s horizontal box without touching vertical virtualization.
 */
export const getLeftRailStyleOverride = (): CSSProperties => ({
  left: LEFT_RAIL_GUTTER_PX,
  width: `calc(100% - ${LEFT_RAIL_GUTTER_PX}px)`,
  overflow: "visible",
});
